import { apiRequest } from './client'
import { demoRfqUpdatesPage } from '../dev/buyerDemoData'
import { demoUiDelay, isDemoUiEnabled, withDemoUi } from '../dev/demoMode'
import { readInt, readString } from './json'

export type RfqUpdateDto = {
  id: number
  rfqId: number
  supplierOrganizationId: number
  supplierOrganizationName: string | null
  eventType: string
  message: string
  read: boolean
  createdAt: string | null
}

export type RfqUpdatesPageDto = {
  items: RfqUpdateDto[]
  page: number
  size: number
  totalElements: number
  totalPages: number
}

type Raw = Record<string, unknown>

const normalizeUpdate = (json: Raw): RfqUpdateDto => ({
  id: readInt(json, 'id', 'id'),
  rfqId: readInt(json, 'rfqId', 'rfq_id'),
  supplierOrganizationId: readInt(json, 'supplierOrganizationId', 'supplier_organization_id'),
  supplierOrganizationName: readString(
    json,
    'supplierOrganizationName',
    'supplier_organization_name',
  ),
  eventType: readString(json, 'eventType', 'event_type') ?? '',
  message: (json.message as string) ?? '',
  read: (json.read as boolean) ?? false,
  createdAt: readString(json, 'createdAt', 'created_at'),
})

const asUpdateRows = (value: unknown): Raw[] => {
  if (Array.isArray(value)) {
    return value as Raw[]
  }
  if (value && typeof value === 'object') {
    const record = value as Raw
    if (Array.isArray(record.items)) {
      return record.items as Raw[]
    }
    if (Array.isArray(record.content)) {
      return record.content as Raw[]
    }
  }
  return []
}

const normalizePage = (json: unknown): RfqUpdatesPageDto => {
  if (Array.isArray(json)) {
    return {
      items: json.map((row) => normalizeUpdate(row as Raw)),
      page: 0,
      size: json.length,
      totalElements: json.length,
      totalPages: 1,
    }
  }
  const record = (json && typeof json === 'object' ? json : {}) as Raw
  const items = asUpdateRows(record)
  return {
    items: items.map(normalizeUpdate),
    page: Number(record.page ?? 0),
    size: Number(record.size ?? items.length),
    totalElements: Number(record.totalElements ?? record.total_elements ?? items.length),
    totalPages: Number(record.totalPages ?? record.total_pages ?? 1),
  }
}

export const listRfqUpdates = async (
  organizationId: number,
  page = 0,
  size = 50,
): Promise<RfqUpdatesPageDto> =>
  withDemoUi(demoRfqUpdatesPage(), async () => {
  const result = await apiRequest<unknown>(
    `/rfqs/updates?organizationId=${organizationId}&page=${page}&size=${size}`,
  )
  return normalizePage(result)
})

export const markRfqUpdateRead = async (organizationId: number, updateId: number) => {
  if (isDemoUiEnabled) {
    await demoUiDelay()
    return
  }
  await apiRequest<void>(`/rfqs/updates/${updateId}/read?organizationId=${organizationId}`, {
    method: 'PATCH',
  })
}

export const markAllRfqUpdatesRead = async (organizationId: number) => {
  if (isDemoUiEnabled) {
    await demoUiDelay()
    return
  }
  await apiRequest<void>(`/rfqs/updates/read-all?organizationId=${organizationId}`, {
    method: 'PATCH',
  })
}

export const clearRfqUpdates = async (organizationId: number) => {
  if (isDemoUiEnabled) {
    await demoUiDelay()
    return
  }
  await apiRequest<void>(`/rfqs/updates?organizationId=${organizationId}`, {
    method: 'DELETE',
  })
}
